/**
 * <s-rental-dates> turns a pickup and return date into the rental day count.
 * The day count is written to the form's quantity input, so the cart line
 * is priced as days x daily price; dates, mileage and the price estimate are sent with the reservation request.
 */
class RentalDates extends HTMLElement {
  #start;
  #end;
  #quantity;
  #summary;

  connectedCallback() {
    this.#start = this.querySelector('[data-rental-start]');
    this.#end = this.querySelector('[data-rental-end]');
    this.#quantity = this.querySelector('[data-rental-quantity]');
    this.#summary = this.querySelector('[data-rental-summary]');
    if (!this.#start || !this.#end || !this.#quantity) return;

    const today = RentalDates.#iso(new Date());
    this.#start.min = today;
    this.#end.min = today;

    this.addEventListener('change', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#update);
  }

  #update = () => {
    if (this.#start.value) {
      const nextDay = new Date(`${this.#start.value}T00:00:00`);
      nextDay.setDate(nextDay.getDate() + 1);
      this.#end.min = RentalDates.#iso(nextDay);
      if (this.#end.value && this.#end.value < this.#end.min) this.#end.value = '';
    }

    const days = this.#days();
    this.#quantity.value = String(days || 1);
    this.#end.setCustomValidity(this.#start.value && this.#end.value && !days ? this.dataset.invalidMessage || '' : '');

    this.#updateKm(days);
    const summaryInput = this.querySelector('[data-rental-summary-input]');

    if (!this.#summary) return;
    if (!days) {
      if (summaryInput) summaryInput.value = '';
      const empty = document.createElement('p');
      empty.className = 'text-base font-semibold text-accent';
      empty.textContent = this.dataset.emptyMessage || '';
      this.#summary.replaceChildren(empty);
      return;
    }

    const locale = document.documentElement.lang || 'de';
    const money = (cents) => new Intl.NumberFormat(locale, { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
    const dailyCents = Number(this.dataset.priceCents || 0);
    const dayLabel = days === 1 ? this.dataset.dayOne : this.dataset.dayOther;
    const rows = [[`${days} ${dayLabel} × ${money(dailyCents)}`, money(dailyCents * days)]];
    const kmPerDay = Number(this.dataset.kmPerDay || 0);
    const formatKm = (value) => `${new Intl.NumberFormat(locale).format(value)} km`;
    if (kmPerDay) rows.push([`${this.dataset.kmIncludedLabel || ''} (${days} × ${formatKm(kmPerDay)})`.trim(), formatKm(kmPerDay * days)]);

    let addonCents = 0;
    let extraKm = 0;
    for (const addon of this.querySelectorAll('input[data-addon-variant]:checked')) {
      const price = Number(addon.dataset.addonPrice || 0);
      addonCents += price;
      extraKm += Number(addon.dataset.addonKm || 0);
      rows.push([`${this.dataset.addonLabel || ''} ${addon.dataset.addonTitle || ''}`.trim(), `+${money(price)}`]);
    }
    if (kmPerDay && extraKm) rows.push([this.dataset.kmTotalLabel || '', formatKm(kmPerDay * days + extraKm)]);

    // The store's automatic 10 % order discount applies to the whole order, so the total includes it.
    const totalCents = Math.round((dailyCents * days + addonCents) * 0.9);

    const lines = rows.map(([label, value]) => RentalDates.#row(label, value, 'text-on-surface/70'));
    const totalRow = RentalDates.#row(this.dataset.totalLabel || '', money(totalCents), 'border-t border-border pt-2 text-base font-semibold text-on-surface');
    const discount = document.createElement('span');
    discount.className = 'ms-1 text-sale';
    discount.textContent = this.dataset.discountLabel || '';
    totalRow.firstElementChild.append(discount);
    // Show the undiscounted total struck through before the discounted one.
    const fullCents = dailyCents * days + addonCents;
    if (fullCents > totalCents) {
      const original = document.createElement('s');
      original.className = 'me-2 text-sm font-normal text-on-surface/60';
      original.textContent = money(fullCents);
      totalRow.lastElementChild.prepend(original);
    }
    this.#summary.replaceChildren(...lines, totalRow);

    if (summaryInput) {
      const plain = rows.map(([label, value]) => `${label}: ${value}`);
      plain.push(`${this.dataset.totalLabel || ''} ${this.dataset.discountLabel || ''}: ${money(totalCents)} (statt ${money(fullCents)})`.trim());
      summaryInput.value = plain.join(' | ');
    }
  };

  static #row(label, value, className) {
    const row = document.createElement('p');
    row.className = `flex items-baseline justify-between gap-3 ${className}`;
    const start = document.createElement('span');
    start.textContent = label;
    const end = document.createElement('span');
    end.className = 'shrink-0 tabular-nums';
    end.textContent = value;
    row.append(start, end);
    return row;
  }

  /** Free kilometres = days x included km per day + checked mileage packages. */
  #updateKm(days) {
    const perDay = Number(this.dataset.kmPerDay || 0);
    const kmText = this.querySelector('[data-rental-km]');
    const kmInput = this.querySelector('[data-rental-km-input]');
    if (!perDay) return;

    let extraKm = 0;
    for (const addon of this.querySelectorAll('input[data-addon-variant]:checked')) {
      extraKm += Number(addon.dataset.addonKm || 0);
    }

    const format = (value) => new Intl.NumberFormat(document.documentElement.lang || 'de').format(value);
    if (!days) {
      if (kmText) kmText.textContent = `${format(perDay)} km ${this.dataset.kmLabel || ''} / ${this.dataset.dayOne || ''}`;
      if (kmInput) kmInput.value = '';
      return;
    }

    const totalKm = perDay * days + extraKm;
    let breakdown = `${days} × ${format(perDay)} km`;
    if (extraKm) breakdown += ` + ${format(extraKm)} km`;
    if (kmText) kmText.textContent = `${format(totalKm)} km ${this.dataset.kmLabel || ''} (${breakdown})`;
    if (kmInput) kmInput.value = `${format(totalKm)} km`;
  }

  #days() {
    if (!this.#start.value || !this.#end.value) return 0;
    const start = new Date(`${this.#start.value}T00:00:00`);
    const end = new Date(`${this.#end.value}T00:00:00`);
    const days = Math.round((end - start) / 86400000);
    return days > 0 ? days : 0;
  }

  static #iso(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
}

if (!customElements.get('s-rental-dates')) customElements.define('s-rental-dates', RentalDates);
